import React, { useState } from "react";
import MiniRangeChart from "./MiniRangeChart";

/**
 * MetricProgressBarCard
 * Displays a single health metric (stress, steps, sleep) with:
 *  - A value + unit
 *  - A status badge
 *  - A progress bar toward the goal
 *  - A collapsible MiniRangeChart for week-over-week comparison
 *
 * Note: uses a pure CSS/Tailwind collapse (overflow-hidden + max-height)
 * instead of reactstrap's Collapse to avoid Bootstrap CSS dependency.
 */
const MetricProgressBarCard = ({
  title,
  numberVal,
  unitVal,
  badgeText,
  progressPercent,
  icon,
  chartData,
  dateRange,
}) => {
  const [expanded, setExpanded] = useState(false);
  const [showComparison, setShowComparison] = useState(false);

  return (
    <div className="bg-white border border-slate-100 rounded-2xl p-4 shadow-sm flex flex-col justify-between transition-all duration-300 ease-in-out hover:shadow-md">
      <div>
        {/* Header row */}
        <div className="flex justify-between items-center w-full">
          <div className="w-8 h-8 bg-[#1D1F3F] rounded-lg flex items-center justify-center text-white">
            <div className="w-5 h-5 rounded-full border border-white/20 flex items-center justify-center bg-white/10">
              {icon}
            </div>
          </div>
          <span className="px-3 py-1 text-[12px] font-semibold rounded-full border bg-emerald-50 text-emerald-700 border-emerald-200 whitespace-nowrap">
            {badgeText}
          </span>
        </div>

        {/* Title */}
        <span className="text-x font-bold text-slate-400 uppercase tracking-wider block mt-3 font-display">
          {title}
        </span>

        {/* Value */}
        <div className="flex items-baseline gap-0.5 mt-0.5 font-display">
          <span className="text-2xl font-extrabold text-slate-900 tracking-tight">
            {numberVal}
          </span>
          <span className="text-xl font-semibold text-slate-500">{unitVal}</span>
        </div>
      </div>

      {/* Progress bar */}
      <div className="mt-4">
        <div className="flex justify-between text-[9px] text-slate-400 font-bold uppercase tracking-wider mb-1.5 font-display">
          <span>Tu empresa</span>
          <span>Objetivo</span>
        </div>
        <div className="w-full h-1.5 bg-slate-100 rounded-full overflow-hidden">
          <div
            className="h-full rounded-full transition-all duration-500 bg-[#1D1F3F]"
            style={{ width: `${progressPercent}%` }}
          />
        </div>
      </div>

      {/* Collapsible chart — pure CSS, no Bootstrap dependency */}
      <div
        style={{
          overflow: "hidden",
          maxHeight: expanded ? "320px" : "0px",
          transition: "max-height 0.3s ease-in-out, opacity 0.3s ease-in-out",
          opacity: expanded ? 1 : 0,
        }}
      >
        <div className="mt-3 pt-3 border-t border-slate-100">
          {/* Legend + comparison toggle */}
          <div className="flex justify-between items-center gap-3 mb-3">
            <div className="flex gap-3">
              <span
                className={`flex items-center gap-1 text-[10px] text-slate-400 transition-opacity duration-200 ${
                  showComparison ? "opacity-100" : "opacity-30"
                }`}
              >
                <span
                  className="w-2.5 h-2.5 rounded-full inline-block"
                  style={{ background: chartData?.color1 }}
                />
                Semana pasada
              </span>
              <span className="flex items-center gap-1 text-[10px] text-slate-400">
                <span
                  className="w-2.5 h-2.5 rounded-full inline-block"
                  style={{ background: chartData?.color2 }}
                />
                Semana actual
              </span>
            </div>

            <label className="relative inline-flex items-center cursor-pointer select-none">
              <input
                type="checkbox"
                className="sr-only peer"
                checked={showComparison}
                onChange={() => setShowComparison(!showComparison)}
              />
              <div className="w-7 h-4 bg-slate-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-3 after:w-3 after:transition-all peer-checked:bg-[#1D1F3F]" />
              <span className="ml-1.5 text-[9px] font-bold text-slate-400 uppercase tracking-wider">
                Comparar
              </span>
            </label>
          </div>

          {chartData && (
            <MiniRangeChart
              s1={chartData.s1}
              s2={chartData.s2}
              color1={chartData.color1}
              color2={chartData.color2}
              domainMin={chartData.domainMin}
              domainMax={chartData.domainMax}
              tickFn={chartData.tickFn ?? ((v) => v)}
              tooltipFn={chartData.tooltipFn}
              showComparison={showComparison}
              dateRange={dateRange}
            />
          )}
        </div>
      </div>

      {/* Expand / collapse button */}
      <div className="flex justify-end mt-2">
        <button
          onClick={() => setExpanded((v) => !v)}
          className={`text-slate-300 hover:text-slate-500 transition-transform duration-200 ${
            expanded ? "rotate-180" : ""
          }`}
          aria-label={expanded ? "Ocultar gráfica" : "Ver gráfica"}
        >
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <polyline points="6 9 12 15 18 9" />
          </svg>
        </button>
      </div>
    </div>
  );
};

export default MetricProgressBarCard;
